import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { cn } from '../lib/utils';
import { getProductDisplayName, getUrlsFromJsonb } from '../lib/helpers';
import { toast } from 'sonner';
import { 
  Video, 
  ImageIcon, 
  Filter, 
  X, 
  Loader2,
  ArrowRight
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useTranslation } from 'react-i18next';

export default function AdsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [ugcAds, setUgcAds] = useState([]);
  const [ugcFullVideos, setUgcFullVideos] = useState([]);
  const [ugcShorts, setUgcShorts] = useState([]);
  const [staticAds, setStaticAds] = useState([]);
  const [winningDetails, setWinningDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProduct, setFilterProduct] = useState('all');
  const [activeTab, setActiveTab] = useState('ugc');
  const { t } = useTranslation();

  const fetchData = useCallback(async () => {
    try {
      const results = await Promise.all([
        supabase.from('products').select('ecom_id, url, platform, image_urls'),
        supabase.from('ugc_ads').select('*'),
        supabase.from('ugc_full_videos').select('*'),
        supabase.from('ugc_shorts').select('*'),
        supabase.from('static_ads').select('*'),
        supabase.from('winning_product_details').select('ecom_id, product_name'),
      ]);

      setProducts(results[0].data || []);
      setUgcAds(results[1].data || []);
      setUgcFullVideos(results[2].data || []);
      setUgcShorts(results[3].data || []);
      setStaticAds(results[4].data || []);
      setWinningDetails(results[5].data || []);
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast.error('Failed to load ads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getProductInfo = (ecomId) => products.find(p => p.ecom_id === ecomId);
  const getFullVideos = (ecomId) => ugcFullVideos.find(v => v.ecom_id === ecomId);
  const getShorts = (ecomId) => ugcShorts.find(s => s.ecom_id === ecomId);

  const filteredUgcAds = filterProduct === 'all' 
    ? ugcAds 
    : ugcAds.filter(ad => ad.ecom_id === filterProduct);

  const filteredStaticAds = filterProduct === 'all'
    ? staticAds
    : staticAds.filter(ad => ad.ecom_id === filterProduct);

  const countStaticImages = (ad) => {
    let count = 0;
    for (let i = 1; i <= 8; i++) {
      const images = getUrlsFromJsonb(ad[`ad${i}_images`]);
      count += images.length;
    }
    return count;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="ads-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1E1B4B] font-['Manrope']">
              {t('ads.title')}
            </h1>
            <p className="text-slate-500 mt-1">
              {t('ads.subtitle', { ugc: ugcAds.length, staticCount: staticAds.length })}
            </p>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-slate-500" />
            <Select value={filterProduct} onValueChange={setFilterProduct}>
              <SelectTrigger className="w-[220px]" data-testid="product-filter">
                <SelectValue placeholder={t('ads.filterPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('ads.allProducts')}</SelectItem>
                {products.map(p => (
                  <SelectItem key={p.ecom_id} value={p.ecom_id}>
                    {getProductDisplayName(p.ecom_id, winningDetails)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterProduct !== 'all' && (
              <button onClick={() => setFilterProduct('all')} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="ugc" className="data-[state=active]:bg-white flex items-center gap-2">
              <Video className="w-4 h-4" />
              {t('ads.ugcTab', { count: filteredUgcAds.length })}
            </TabsTrigger>
            <TabsTrigger value="static" className="data-[state=active]:bg-white flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              {t('ads.staticTab', { count: filteredStaticAds.length })}
            </TabsTrigger>
          </TabsList>

          {/* UGC Tab */}
          <TabsContent value="ugc">
            {filteredUgcAds.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Video className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  {t('ads.ugc.emptyTitle')}
                </h3>
                <p className="text-slate-500">
                  {t('ads.ugc.emptySubtitle')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredUgcAds.map((ad) => {
                  const productInfo = getProductInfo(ad.ecom_id);
                  const fullVideos = getFullVideos(ad.ecom_id);
                  const shorts = getShorts(ad.ecom_id);
                  const displayName = getProductDisplayName(ad.ecom_id, winningDetails);
                  const videoCount = [
                    fullVideos?.video1, fullVideos?.video2, fullVideos?.video3, fullVideos?.video4,
                    shorts?.video1, shorts?.video2, shorts?.video3, shorts?.video4
                  ].filter(Boolean).length;

                  return (
                    <div
                      key={ad.id}
                      onClick={() => navigate(`/ads/ugc/${ad.ecom_id}`)}
                      className={cn(
                        'bg-white rounded-xl border border-slate-200 overflow-hidden',
                        'hover:shadow-lg hover:border-violet-300 transition-all cursor-pointer group'
                      )}
                      data-testid={`ugc-ad-card-${ad.ecom_id}`}
                    >
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-bold text-[#1E1B4B] line-clamp-1">{displayName}</h3>
                            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium text-slate-600 capitalize mt-1 inline-block">
                              {productInfo?.platform || 'Unknown'}
                            </span>
                          </div>
                          <StatusBadge status={ad.status} lane="ugc" />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="p-3 bg-violet-50 rounded-lg">
                            <p className="text-xs text-violet-600 font-medium">
                              {t('ads.fullVideosLabel')}
                            </p>
                            <p className="text-lg font-bold text-violet-700">
                              {[fullVideos?.video1, fullVideos?.video2, fullVideos?.video3, fullVideos?.video4].filter(Boolean).length}/4
                            </p>
                          </div>
                          <div className="p-3 bg-violet-50 rounded-lg">
                            <p className="text-xs text-violet-600 font-medium">
                              {t('ads.shortsLabel')}
                            </p>
                            <p className="text-lg font-bold text-violet-700">
                              {[shorts?.video1, shorts?.video2, shorts?.video3, shorts?.video4].filter(Boolean).length}/4
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Video className="w-4 h-4" />
                            <span>{t('ads.videosReady', { count: videoCount })}</span>
                          </div>
                          <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-violet-500 transition-colors" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Static Ads Tab */}
          <TabsContent value="static">
            {filteredStaticAds.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <ImageIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  {t('ads.static.emptyTitle')}
                </h3>
                <p className="text-slate-500">
                  {t('ads.static.emptySubtitle')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredStaticAds.map((ad) => {
                  const productInfo = getProductInfo(ad.ecom_id);
                  const displayName = getProductDisplayName(ad.ecom_id, winningDetails);
                  const imageCount = countStaticImages(ad);
                  const previewImages = [];
                  for (let i = 1; i <= 8 && previewImages.length < 4; i++) {
                    const images = getUrlsFromJsonb(ad[`ad${i}_images`]);
                    previewImages.push(...images.slice(0, 4 - previewImages.length));
                  }

                  return (
                    <div
                      key={ad.id}
                      onClick={() => navigate(`/ads/static/${ad.ecom_id}`)}
                      className={cn(
                        'bg-white rounded-xl border border-slate-200 overflow-hidden',
                        'hover:shadow-lg hover:border-amber-300 transition-all cursor-pointer group'
                      )}
                      data-testid={`static-ad-card-${ad.ecom_id}`}
                    >
                      {/* Preview Grid */}
                      <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100">
                        {[0, 1, 2, 3].map(i => (
                          <div key={i} className="aspect-square bg-slate-200 rounded overflow-hidden">
                            {previewImages[i] ? (
                              <img src={previewImages[i]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon className="w-6 h-6 text-slate-400" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-bold text-[#1E1B4B] line-clamp-1">{displayName}</h3>
                            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium text-slate-600 capitalize mt-1 inline-block">
                              {productInfo?.platform || 'Unknown'}
                            </span>
                          </div>
                          <StatusBadge status={ad.status} lane="ads" />
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                              <ImageIcon className="w-4 h-4" />
                              <span>{t('ads.imagesCount', { count: imageCount })}</span>
                            </div>
                            <span>{t('ads.adSets')}</span>
                          </div>
                          <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-amber-500 transition-colors" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
